import { useNotification, RangeModifier } from '@wheely/ui-kit';
import endOfWeek from 'date-fns/endOfWeek';
import startOfWeek from 'date-fns/startOfWeek';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { usePermissions } from '@/app/hooks/usePermissions';
import { IntlContext } from '@/i18n';
import { resetDriver } from '@/modules/driver/actions/actions';
import { blockDriver } from '@/modules/driver/actions/async/block-driver';
import { changeContractStatus } from '@/modules/driver/actions/async/change-contract-status';
import { dismissDriver } from '@/modules/driver/actions/async/dismissDriver';
import { loadDriverById } from '@/modules/driver/actions/async/load-driver-by-id';
import { loadWorkload } from '@/modules/driver/actions/async/loadWorkload';
import { prepareUploadDriverForm } from '@/modules/driver/actions/async/prepare-upload-driver-form';
import { DriverDocType } from '@/modules/driver/model/driver-doc-type';
import {
  getCurrentDriver,
  getHourlyWorkloads,
  getWorkloadSummary,
} from '@/modules/driver/store/selectors';
import { driverUploadSelectors } from '@/modules/driver/store/upload-slice';
import { AppDispatch } from '@/root-store';
import { UploadDriver } from '@/shared/view/Upload';
import { useCollapsedDocsList } from '@/shared/view/Upload/SupplementaryDocs/useCollapsedDocsList';
import { IDocsListItem } from '@src-common/view/components/DocsList';

import { useOpenBlockingModal } from './components/Blocking';
import { useOpenDismissDriverDialog } from './components/DismissDriverDialog';
import { View } from './view';

interface IProps {
  onNavigateBack: (uri: string) => void;
}

const ROUTE = '/chauffeurs/:driverId';

const DriverDetails = ({ onNavigateBack }: IProps) => {
  usePermissions('viewDrivers');

  const driverId = useRouteMatch<{ driverId: string }>(ROUTE)?.params?.driverId;

  const dispatch: AppDispatch = useDispatch();
  const intl = useIntl();
  const [workingHoursWeek, setWorkingHoursWeek] = useState<RangeModifier>({
    from: startOfWeek(new Date(), { weekStartsOn: 1 }),
    to: endOfWeek(new Date(), { weekStartsOn: 1 }),
  });

  useEffect(() => {
    if (!driverId) {
      return;
    }

    dispatch(loadDriverById(driverId));

    return () => {
      dispatch(resetDriver());
    };
  }, [dispatch, driverId]);

  useEffect(() => {
    if (!driverId) {
      return;
    }

    dispatch(loadWorkload({ driverId, from: workingHoursWeek?.from, to: workingHoursWeek?.to }));
  }, [dispatch, workingHoursWeek, driverId]);

  const driver = useSelector(getCurrentDriver);
  const docs = useCollapsedDocsList(useSelector(driverUploadSelectors.getDocsList), 'driver');
  const hourlyWorkloads = useSelector(getHourlyWorkloads);
  const workloadSummary = useSelector(getWorkloadSummary);

  const handleClickBack = useCallback(() => onNavigateBack('/todo'), [onNavigateBack]);

  const { openDismissDriverDialog, closeDismissDriverDialog } = useOpenDismissDriverDialog();
  const { showNotification } = useNotification();

  const [isPending, setIsPending] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  const handleUploadDoc = useCallback(
    (_: any, doc: IDocsListItem) => {
      dispatch(
        prepareUploadDriverForm({
          docType: doc.docType as DriverDocType,
          onSuccess: () => setIsUploading(true),
        }),
      );
    },
    [dispatch],
  );

  const handleSkipUpload = useCallback(() => {
    setIsUploading(false);
  }, []);

  const handleFinishUpload = useCallback(() => {
    dispatch(loadDriverById(driverId));
    setIsUploading(false);
  }, [dispatch, driverId]);

  const { openBlocking, closeDialogs } = useOpenBlockingModal();

  const handleBlock = useCallback(
    async (reason: string, message: string) => {
      if (!driverId) {
        return;
      }

      try {
        setIsPending(true);
        await dispatch(blockDriver({ driverId, reason, message })).unwrap();
      } finally {
        setIsPending(false);
        closeDialogs();
      }
    },
    [closeDialogs, dispatch, driverId],
  );

  const handleChangeContractStatus = useCallback(
    (isActive: boolean) => {
      dispatch(changeContractStatus({ driverId, isActive }));
    },
    [dispatch, driverId],
  );

  const handleStartBlocking = useCallback(() => {
    openBlocking({ onBlock: handleBlock });
  }, [handleBlock, openBlocking]);

  const handleDismissDriver = useCallback(async () => {
    if (!driverId) {
      return;
    }

    const { isAlreadyExecuted } = await dispatch(dismissDriver({ driverId })).unwrap();

    if (isAlreadyExecuted) {
      return;
    }

    closeDismissDriverDialog();

    showNotification({
      message: intl.formatMessage({
        defaultMessage: 'Chauffeur released',
        id: 'eE4wa5',
        description: 'Dismiss driver success notification',
      }),
      showCloseButton: false,
      dark: true,
    });
  }, [dispatch, driverId, closeDismissDriverDialog, intl, showNotification]);

  const handleStartDismissDriver = useCallback(() => {
    openDismissDriverDialog({ onDismiss: handleDismissDriver });
  }, [handleDismissDriver, openDismissDriverDialog]);

  const { region } = useContext(IntlContext);

  if (!driver) {
    return null;
  }

  return (
    <>
      <View
        onChangeContract={handleChangeContractStatus}
        region={region}
        docs={docs}
        onUploadDoc={handleUploadDoc}
        driver={driver}
        onClickBlock={handleStartBlocking}
        onClickBack={handleClickBack}
        onClickDismiss={handleStartDismissDriver}
        isPending={isPending}
        workloads={hourlyWorkloads}
        workloadSummary={workloadSummary}
        workingHoursWeek={workingHoursWeek}
        onWorkingHoursWeekChange={setWorkingHoursWeek}
      />
      {isUploading && (
        <UploadDriver onClickSkip={handleSkipUpload} onFinishUpload={handleFinishUpload} />
      )}
    </>
  );
};

const withRoute = Object.assign(DriverDetails, { ROUTE });

export { withRoute as DriverDetails };
